import { MobXProviderContext } from "mobx-react";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { getErrorTitle } from "../../../../constants/constants";
import { CareServicesSection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import { appAccentPanelStyle, appDialogContentStyle, appDialogHeaderStyle, appDialogStyle, appFormSectionStyle, appMutedTextStyle, renderDialogFooter, renderSurfaceHeader } from "../../../../components/general/uiSurfaceStyles";

interface Props {
  data: CareServicesSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const splitLines = (value: string) => value.split("\n").map((item) => normalizePlainText(item, { maxLength: 180 })).filter(Boolean);

const EditModal: FC<Props> = ({ data, isOpen, toggleModal, flag, setFlag }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      specializedCare: (data?.specializedCare || []).join("\n"),
      mealsAndNutrition: (data?.mealsAndNutrition || []).join("\n"),
      dailyLifeHighlights: (data?.dailyLifeHighlights || []).join("\n"),
      careTeam: (data?.careTeam || []).join("\n"),
    },
  });

  useEffect(() => {
    reset({
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      specializedCare: (data?.specializedCare || []).join("\n"),
      mealsAndNutrition: (data?.mealsAndNutrition || []).join("\n"),
      dailyLifeHighlights: (data?.dailyLifeHighlights || []).join("\n"),
      careTeam: (data?.careTeam || []).join("\n"),
    });
  }, [data, isOpen, reset]);

  const onSubmit = async (formData: any) => {
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateTemplateSection({
        ...data,
        enabled: !!formData.enabled,
        title: normalizePlainText(formData.title, { maxLength: 140 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        specializedCare: splitLines(formData.specializedCare),
        mealsAndNutrition: splitLines(formData.mealsAndNutrition),
        dailyLifeHighlights: splitLines(formData.dailyLifeHighlights),
        careTeam: splitLines(formData.careTeam),
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the care services page.", life: 3000 });
    }
  };

  return (
    <Dialog visible={isOpen} modal header={renderSurfaceHeader("Care Services Page", "Manage specialized care, meals, daily life, and team details.", "pi pi-heart")} footer={renderDialogFooter({ errorMessage: null, loading: false, onCancel: () => toggleModal(false), onSubmit: handleSubmit(onSubmit), submitLabel: "Save Care Services" })} style={appDialogStyle} headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle} onHide={() => toggleModal(false)}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#185da2" }}>Care services page</div>
        <div className="mt-2" style={appMutedTextStyle}>Use this page to explain what life and care support look like inside the home.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller name="enabled" control={control} render={({ field }) => <Checkbox inputId="careServicesEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />} />
            <label htmlFor="careServicesEnabled" style={{ fontWeight: 600 }}>Add Care Services page into the website</label>
          </div>
        </div>
        {[
          { name: "title", label: "Page Title", rows: 3 },
          { name: "description", label: "Description", rows: 5 },
          { name: "specializedCare", label: "Specialized Care", rows: 5 },
          { name: "mealsAndNutrition", label: "Meals and Nutrition", rows: 5 },
          { name: "dailyLifeHighlights", label: "Daily Life", rows: 5 },
          { name: "careTeam", label: "Care Team", rows: 5 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller name={name as any} control={control} render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} />} />
          </div>
        ))}
      </form>
    </Dialog>
  );
};

export default EditModal;
